import { Fetcher } from '@this/src/util';

export type TicketCategory = 'changeable' | 'unchangeable';
export type PreferredTransportType = 'rail' | 'air';
export type ProposalBundleState = 'suggesting' | 'selected' | 'rejected';

export const TICKET_CATEGORY_LABELS: Record<TicketCategory, string> = {
  changeable: '変更可',
  unchangeable: '変更不可'
} as const;

export const getTicketCategoryLabel = (category: TicketCategory): string => {
  return TICKET_CATEGORY_LABELS[category];
};

export const PROPOSAL_BUNDLE_STATE = {
  SUGGESTING: 'suggesting',
  SELECTED: 'selected',
  REJECTED: 'rejected'
} as const;

export const PROPOSAL_BUNDLE_CATEGORY = {
  TRANSPORT: 'transport',
  HOTEL: 'hotel',
  PACKAGE: 'package',
  OUTWARD: 'outward',
  HOMEWARD: 'homeward'
} as const;

export type ProposalBundleCategory = typeof PROPOSAL_BUNDLE_CATEGORY[keyof typeof PROPOSAL_BUNDLE_CATEGORY];

export type ProposalItem = {
  id: number;
  title: string | null;
  subtitle: string | null;
  departureTime: string | null;
  arrivalTime: string | null;
  flightName: string | null;
  isConnectionFlight: boolean;
  connectionSummary: string | null;
  seatClass: string | null;
  ticketType: string | null;
  ticketCategory: TicketCategory | null;
  price: number | null;
  priceWithTax: number | null;
  priceText: string | null;
  aiHighlightTags: string[];
  isAir: boolean | null;
  isShinkansen: boolean | null;
  stayDays: number | null;
  breakfastExist: boolean | null;
  smoke: boolean | null;
  selected: boolean;
};

export type ProposalBundle = {
  id: number;
  category: ProposalBundleCategory;
  state: ProposalBundleState;
  origin: string | null;
  destination: string | null;
  departureDate: string | null;
  returnDate: string | null;
  stayDays: number | null;
  preferredTransportType: PreferredTransportType | null;
  items: ProposalItem[];
};

type ProposalItemResponse = {
  id: number;
  title: string | null;
  subtitle: string | null;
  departure_time: string | null;
  arrival_time: string | null;
  flight_name: string | null;
  is_connection_flight: boolean;
  connection_summary: string | null;
  seat_class: string | null;
  ticket_type: string | null;
  ticket_category: TicketCategory | null;
  price: number | null;
  price_with_tax: number | null;
  price_text: string | null;
  ai_highlight_tags: string[];
  is_air: boolean | null;
  is_shinkansen: boolean | null;
  stay_days: number | null;
  breakfast_exist: boolean | null;
  smoke: boolean | null;
  selected: boolean;
};

type ProposalBundleResponse = {
  id: number;
  category: ProposalBundleCategory;
  state: 'suggesting' | 'selected' | 'rejected';
  origin: string | null;
  destination: string | null;
  departure_date: string | null;
  return_date: string | null;
  stay_days: number | null;
  preferred_transport_type: PreferredTransportType | null;
  items: ProposalItemResponse[];
};

type FetchProposalBundlesResponse = {
  bundles: ProposalBundleResponse[];
};

const toProposalItem = (item: ProposalItemResponse): ProposalItem => ({
  id: item.id,
  title: item.title,
  subtitle: item.subtitle,
  departureTime: item.departure_time,
  arrivalTime: item.arrival_time,
  flightName: item.flight_name,
  isConnectionFlight: item.is_connection_flight,
  connectionSummary: item.connection_summary,
  seatClass: item.seat_class,
  ticketType: item.ticket_type,
  ticketCategory: item.ticket_category,
  price: item.price,
  priceWithTax: item.price_with_tax,
  priceText: item.price_text,
  aiHighlightTags: item.ai_highlight_tags,
  isAir: item.is_air,
  isShinkansen: item.is_shinkansen,
  stayDays: item.stay_days,
  breakfastExist: item.breakfast_exist,
  smoke: item.smoke,
  selected: item.selected
});

const toProposalBundle = (bundle: ProposalBundleResponse): ProposalBundle => ({
  id: bundle.id,
  category: bundle.category,
  state: bundle.state,
  origin: bundle.origin,
  destination: bundle.destination,
  departureDate: bundle.departure_date,
  returnDate: bundle.return_date,
  stayDays: bundle.stay_days,
  preferredTransportType: bundle.preferred_transport_type,
  items: bundle.items.map(toProposalItem)
});

export const fetchProposalBundles = async (tripId: string): Promise<ProposalBundle[]> => {
  const response = await Fetcher.get<FetchProposalBundlesResponse>(`/trips/${tripId}/ai_proposal_bundles.json`);
  return response.bundles.map(toProposalBundle);
};

type RejectProposalBundleResponse = {
  success: boolean;
  bundle_id: number;
  state: string;
};

export const rejectProposalBundle = async (tripId: string, bundleId: number): Promise<{ success: boolean }> => {
  const response = await Fetcher.patch<RejectProposalBundleResponse>(
    `/trips/${tripId}/ai_proposal_bundles/${bundleId}/reject.json`
  );
  return { success: response.success };
};

type SelectProposalItemResponse = {
  success: boolean;
  bundle_id: number;
  item_id: number;
  state: 'suggesting' | 'selected' | 'rejected';
};

export const selectProposalItem = async (
  tripId: string,
  bundleId: number,
  itemId: number
): Promise<{ success: boolean; state: ProposalBundle['state'] }> => {
  const response = await Fetcher.patch<SelectProposalItemResponse>(
    `/trips/${tripId}/ai_proposal_bundles/${bundleId}/items/${itemId}/select.json`
  );
  return { success: response.success, state: response.state };
};

type SummaryItemResponse = {
  category: 'outward' | 'homeward' | 'hotel';
  label: string;
  text: string;
  price: number | null;
  price_with_tax: number | null;
};

type SummaryResponse = {
  items: SummaryItemResponse[];
  total_price: number;
  total_price_with_tax: number;
  has_selection: boolean;
};

type CompleteProposalSelectionResponse = {
  success: boolean;
  message_id: number | null;
  summary: SummaryResponse | null;
  has_selection: boolean;
  already_completed: boolean;
};

export const completeProposalSelection = async (
  tripId: string
): Promise<{ success: boolean; alreadyCompleted: boolean }> => {
  const response = await Fetcher.post<CompleteProposalSelectionResponse>(
    `/trips/${tripId}/ai_proposal_bundles/complete.json`,
    {}
  );
  return { success: response.success, alreadyCompleted: response.already_completed };
};

export type HotelDetail = {
  name: string;
  address: string;
  images: string[];
  latitude: number;
  longitude: number;
  distance: number;
  nearestStationName: string | null;
  stationDistance: number;
  checkin: string;
  checkout: string;
  tel: string;
  cancelPolicy: string;
  refundable: boolean | null;
  propertyAmenities: string[] | null;
  roomAmenities: string[] | null;
  note: string | null;
  parkingInformation: string | null;
  checkinInstructions: string | null;
  specialCheckinInstructions: string | null;
};

export type DestLocation = {
  lat: number;
  lng: number;
};

export type HotelBedType = {
  description: string;
};

export type HotelPlanDetail = {
  roomName: string;
  planName: string | null;
  planDetail: string | null;
  price: number;
  priceWithTax: number;
  priceText: string;
  smoke: boolean | null;
  bedTypes: HotelBedType[] | null;
  checkinTime: string | null;
};

export type HotelDetailData = {
  hotel: HotelDetail;
  destLocation: DestLocation | null;
  plan: HotelPlanDetail;
};

type HotelDetailResponse = {
  hotel: {
    name: string;
    address: string;
    images: string[];
    latitude: number;
    longitude: number;
    distance: number;
    nearest_station_name: string | null;
    station_distance: number;
    checkin: string;
    checkout: string;
    tel: string;
    cancel_policy: string;
    refundable: boolean | null;
    property_amenities: string[] | null;
    room_amenities: string[] | null;
    note: string | null;
    parking_information: string | null;
    checkin_instructions: string | null;
    special_checkin_instructions: string | null;
  };
  dest_location: { lat: number; lng: number } | null;
  plan: {
    room_name: string;
    plan_name: string | null;
    plan_detail: string | null;
    price: number;
    price_with_tax: number;
    price_text: string;
    smoke: boolean | null;
    bed_types: { description: string }[] | null;
    checkin_time: string | null;
  };
};

const toHotelDetailData = (response: HotelDetailResponse): HotelDetailData => ({
  hotel: {
    name: response.hotel.name,
    address: response.hotel.address,
    images: response.hotel.images,
    latitude: response.hotel.latitude,
    longitude: response.hotel.longitude,
    distance: response.hotel.distance,
    nearestStationName: response.hotel.nearest_station_name,
    stationDistance: response.hotel.station_distance,
    checkin: response.hotel.checkin,
    checkout: response.hotel.checkout,
    tel: response.hotel.tel,
    cancelPolicy: response.hotel.cancel_policy,
    refundable: response.hotel.refundable,
    propertyAmenities: response.hotel.property_amenities,
    roomAmenities: response.hotel.room_amenities,
    note: response.hotel.note,
    parkingInformation: response.hotel.parking_information,
    checkinInstructions: response.hotel.checkin_instructions,
    specialCheckinInstructions: response.hotel.special_checkin_instructions
  },
  destLocation: response.dest_location,
  plan: {
    roomName: response.plan.room_name,
    planName: response.plan.plan_name,
    planDetail: response.plan.plan_detail,
    price: response.plan.price,
    priceWithTax: response.plan.price_with_tax,
    priceText: response.plan.price_text,
    smoke: response.plan.smoke,
    bedTypes: response.plan.bed_types,
    checkinTime: response.plan.checkin_time
  }
});

export const fetchProposalItemHotelDetail = async (
  tripId: string,
  itemId: number,
  signal?: AbortSignal
): Promise<HotelDetailData> => {
  const response = await Fetcher.get<HotelDetailResponse>(
    `/trips/${tripId}/ai_proposal_items/${itemId}/hotel_detail.json`,
    undefined,
    undefined,
    signal
  );
  return toHotelDetailData(response);
};

type GoogleReviewResponse = {
  author_name: string;
  rating: number;
  relative_time_description: string;
  text: string;
};

type GoogleReviewsResponse = {
  rating: number | null;
  user_ratings_total: number | null;
  reviews: GoogleReviewResponse[] | null;
};

type ProposalItemGoogleReviewsResponse = {
  google_reviews: GoogleReviewsResponse | null;
};

export type GoogleReview = {
  authorName: string;
  rating: number;
  relativeTimeDescription: string;
  text: string;
};

export type GoogleReviews = {
  rating: number | null;
  userRatingsTotal: number | null;
  reviews: GoogleReview[];
};

const toGoogleReviews = (response: GoogleReviewsResponse | null): GoogleReviews => {
  if (response == null) {
    return {
      rating: null,
      userRatingsTotal: null,
      reviews: []
    };
  }

  return {
    rating: response.rating,
    userRatingsTotal: response.user_ratings_total,
    reviews: (response.reviews || []).map(review => ({
      authorName: review.author_name,
      rating: review.rating,
      relativeTimeDescription: review.relative_time_description,
      text: review.text
    }))
  };
};

export const fetchProposalItemGoogleReviews = async (
  tripId: string,
  itemId: number,
  signal?: AbortSignal
): Promise<GoogleReviews> => {
  const response = await Fetcher.get<ProposalItemGoogleReviewsResponse>(
    `/trips/${tripId}/ai_proposal_items/${itemId}/google_reviews.json`,
    undefined,
    undefined,
    signal
  );
  return toGoogleReviews(response.google_reviews);
};

export type HotelProposalReasonStatus = 'completed' | 'unavailable';

export type HotelProposalReason = {
  itemId: number;
  reason: string;
};

export type HotelProposalReasonsResult = {
  bundleId: number;
  status: HotelProposalReasonStatus;
  summaryReason: string | null;
  itemReasons: HotelProposalReason[];
};

type HotelProposalReasonResponse = {
  item_id: number;
  reason: string;
};

type HotelProposalReasonsResponse = {
  bundle_id: number;
  status: HotelProposalReasonStatus;
  summary_reason: string | null;
  item_reasons: HotelProposalReasonResponse[];
};

const toHotelProposalReasonsResult = (response: HotelProposalReasonsResponse): HotelProposalReasonsResult => ({
  bundleId: response.bundle_id,
  status: response.status,
  summaryReason: response.summary_reason,
  itemReasons: response.item_reasons.map(entry => ({
    itemId: entry.item_id,
    reason: entry.reason
  }))
});

export const fetchHotelProposalReasons = async (
  tripId: string,
  bundleId: number
): Promise<HotelProposalReasonsResult> => {
  const response = await Fetcher.get<HotelProposalReasonsResponse>(
    `/trips/${tripId}/ai_proposal_bundles/${bundleId}/hotel_reasons.json`
  );
  return toHotelProposalReasonsResult(response);
};
